import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        padding: '0 var(--page-pad)',
        height: 'var(--header-h)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', padding: '6px 0',
            color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 500,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Back
        </button>
        <span style={{ color: 'var(--border)', fontSize: '1.2rem' }}>|</span>
        <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '1rem', color: 'var(--text)' }}>
          Privacy Policy
        </span>
      </div>

      {/* Content */}
      <div style={{ flex: 1, maxWidth: 720, width: '100%', margin: '0 auto', padding: '32px var(--page-pad) 64px' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
          Privacy Policy
        </h1>
        <p style={{ color: 'var(--text-faint)', fontSize: '0.875rem', marginBottom: 40 }}>
          Effective Date: [DATE] &nbsp;·&nbsp; Last Updated: [DATE]
        </p>

        <p style={prose}>
          This Privacy Policy explains how <strong>Coarchitect</strong> ("Company", "we", "us", or "our") collects, uses, stores, and shares information when you use the Coarchitect platform, including the web application, mobile application (iOS and Android), and API (collectively, the "Service").
        </p>
        <p style={prose}>
          By using the Service, you agree to the collection and use of information as described in this Policy.
        </p>

        <Section n="1" title="Information We Collect">
          <p style={sectionLabel}>1.1 Information You Provide</p>
          <Table
            headers={['Category', 'Examples']}
            rows={[
              ['Account information', 'Mobile number, email address, business name, business category, city'],
              ['Credentials', 'Password hash (email/password users); OTP verification (mobile users)'],
              ['Profile updates', 'Business name, business category'],
              ['User Content', 'Images you upload (space photos, product photos, reference images)'],
            ]}
          />
          <p style={{ ...sectionLabel, marginTop: 16 }}>1.2 Information Collected Automatically</p>
          <Table
            headers={['Category', 'Examples']}
            rows={[
              ['Usage data', 'Pages visited, features used, generation history, timestamps'],
              ['Device information', 'IP address, browser type, operating system, device model'],
              ['Log data', 'Server logs, error logs, API request metadata'],
            ]}
          />
          <p style={{ ...sectionLabel, marginTop: 16 }}>1.3 Information from Third Parties</p>
          <p style={prose}>We receive limited technical data from third-party AI image model providers solely in the course of processing your generation requests. We do not receive any personal data from these providers beyond what we send them.</p>
        </Section>

        <Section n="2" title="How We Use Your Information">
          <p style={prose}>We use the information we collect to:</p>
          <ul style={list}>
            <li><strong>Provide the Service</strong> — process your images, generate AI previews, store your projects</li>
            <li><strong>Authenticate you</strong> — verify your identity via OTP or email/password login</li>
            <li><strong>Communicate with you</strong> — send OTP codes, service updates, and security alerts</li>
            <li><strong>Improve the Service</strong> — analyze usage patterns, diagnose errors, and develop new features</li>
            <li><strong>Enforce our Terms</strong> — detect and prevent abuse, fraud, and security incidents</li>
            <li><strong>Comply with legal obligations</strong> — respond to lawful requests from authorities</li>
          </ul>
          <p style={{ ...prose, background: 'var(--accent-light)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', borderLeft: '3px solid var(--accent)' }}>
            We do <strong>not</strong> use your uploaded images or generated outputs to train AI models without your explicit, opt-in consent.
          </p>
        </Section>

        <Section n="3" title="How We Share Your Information">
          <p style={prose}>We do not sell your personal data. We share data only in the following circumstances:</p>
          <p style={sectionLabel}>3.1 Service Providers</p>
          <Table
            headers={['Provider', 'Purpose', 'Data Shared']}
            rows={[
              ['Cloud object storage provider', 'Image storage', 'Uploaded and generated images'],
              ['AI image model providers', 'AI image generation and scene analysis', 'Space images, product images'],
              ['Cloud infrastructure provider', 'Backend infrastructure', 'All service data'],
            ]}
          />
          <p style={{ ...sectionLabel, marginTop: 16 }}>3.2 Legal Requirements</p>
          <p style={prose}>We may disclose your information if required by law, court order, or government request, or if we believe in good faith that disclosure is necessary to protect the rights, property, or safety of the Company, our users, or the public.</p>
          <p style={{ ...sectionLabel, marginTop: 16 }}>3.3 Business Transfers</p>
          <p style={prose}>If the Company is involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email or in-app notice before your data is transferred and becomes subject to a different privacy policy.</p>
        </Section>

        <Section n="4" title="Data Storage and Security">
          <p style={prose}><strong>4.1 Storage.</strong> Your account data is stored in databases hosted on secure cloud infrastructure. Images are stored in cloud object storage.</p>
          <p style={prose}><strong>4.2 Security measures.</strong> We implement the following controls:</p>
          <ul style={list}>
            <li>Passwords hashed with bcrypt (12 rounds)</li>
            <li>JWT-based authentication with short-lived access tokens (15 minutes)</li>
            <li>TLS encryption in transit</li>
            <li>Presigned, expiring URLs for image access</li>
            <li>Rate limiting on all API endpoints</li>
            <li>Regular security reviews</li>
          </ul>
          <p style={prose}><strong>4.3 Retention.</strong> We retain your data for as long as your account is active. Upon account deletion, personal data is deleted within 30 days, except where retention is required by law. Anonymized usage analytics may be retained longer.</p>
          <p style={prose}>No security system is impenetrable. In the event of a data breach that affects your personal information, we will notify you as required by applicable law.</p>
        </Section>

        <Section n="5" title="Your Rights">
          <p style={prose}>Depending on your jurisdiction, you may have the following rights regarding your personal data:</p>
          <Table
            headers={['Right', 'Description']}
            rows={[
              ['Access', 'Request a copy of the personal data we hold about you'],
              ['Correction', 'Request correction of inaccurate or incomplete data'],
              ['Deletion', 'Request deletion of your personal data ("right to be forgotten")'],
              ['Portability', 'Request your data in a machine-readable format'],
              ['Objection', 'Object to certain types of processing'],
              ['Withdrawal', 'Withdraw consent where processing is based on consent'],
            ]}
          />
          <p style={prose}>To exercise any of these rights, contact us at <strong>contact@coarchitect.app</strong>. We will respond within 30 days.</p>
        </Section>

        <Section n="6" title="Children's Privacy">
          <p style={prose}>
            The Service is not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected data from a minor, contact us immediately at <strong>contact@coarchitect.app</strong> and we will delete it promptly.
          </p>
        </Section>

        <Section n="7" title="Cookies and Tracking">
          <p style={prose}>The web application may use cookies and similar technologies to:</p>
          <ul style={list}>
            <li>Maintain your session state (authentication)</li>
            <li>Remember your preferences</li>
            <li>Analyze usage patterns (analytics)</li>
          </ul>
          <p style={prose}>You can control cookie settings through your browser. Disabling certain cookies may affect Service functionality.</p>
          <p style={prose}>We do not use third-party advertising cookies or cross-site tracking.</p>
        </Section>

        <Section n="8" title="Third-Party Links">
          <p style={prose}>
            The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of those third parties. We encourage you to review their privacy policies before providing any personal information.
          </p>
        </Section>

        <Section n="9" title="International Data Transfers">
          <p style={prose}>
            Your data may be processed in countries other than your country of residence, including the United States, where our AI providers and infrastructure providers operate. Where required by law, we ensure appropriate safeguards (such as standard contractual clauses) are in place for such transfers.
          </p>
        </Section>

        <Section n="10" title="Changes to This Policy">
          <p style={prose}>
            We may update this Privacy Policy from time to time. We will notify you of material changes by email or in-app notification at least 14 days before the changes take effect. The "Last Updated" date at the top of this page reflects the most recent revision.
          </p>
        </Section>

        <Section n="11" title="Contact and Grievance Officer">
          <p style={prose}>
            <strong>Coarchitect</strong><br />
            Email: <strong>contact@coarchitect.app</strong>
          </p>
          <p style={prose}>We will acknowledge receipt of your query within 72 hours and resolve it within 30 days.</p>
        </Section>

        {/* Footer nav */}
        <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--border)', display: 'flex', gap: 16, fontSize: '0.875rem', color: 'var(--text-faint)' }}>
          <Link to="/terms" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Terms of Service</Link>
          <span>·</span>
          <Link to="/" style={{ color: 'var(--text-muted)' }}>Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 36 }}>
      <h2 style={{
        fontFamily: 'var(--font-heading)', fontWeight: 600,
        fontSize: '1.125rem', color: 'var(--text)',
        marginBottom: 12, paddingBottom: 8,
        borderBottom: '1px solid var(--border)',
      }}>
        {n}. {title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', marginTop: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ background: 'var(--surface-alt)' }}>
            {headers.map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--bg)' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ padding: '8px 12px', color: 'var(--text)', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none', lineHeight: 1.5 }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const prose: React.CSSProperties = {
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  color: 'var(--text)',
};

const sectionLabel: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  letterSpacing: '0.01em',
};

const list: React.CSSProperties = {
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: 'var(--text)',
};

export default PrivacyPolicy;
